(() => {
  'use strict';

  if (window.orderExtrasInitialized) return;
  window.orderExtrasInitialized = true;

  const PRODUCT_FORM_SELECTOR = 'product-form form[action*="/cart/add"]';

  const GIFT_VARIANT_SELECTORS = [
    '[data-digital-gift-variant-id]',
    '[data-gift-variant-id]',
    '.order-digital-gifts [data-variant-id]',
    '.digital-gifts [data-variant-id]'
  ];

  const OPTIONAL_CHECKBOX_SELECTORS = [
    '[data-optional-checkbox]',
    '[data-optional-product-checkbox]',
    'input[name="optional_product"]',
    'input[name="add_optional_product"]',
    '.order-optional input[type="checkbox"]'
  ];

  const OPTIONAL_VARIANT_SELECTORS = [
    '[data-optional-product-variant-id]',
    '.order-optional [data-variant-id]',
    'input[name="optional_variant_id"]'
  ];

  function getRootUrl() {
    return window.Shopify?.routes?.root || '/';
  }

  function getCartAddUrl() {
    return window.routes?.cart_add_url || `${getRootUrl()}cart/add.js`;
  }

  function getSelectedMainVariantId(form) {
    const variantInput = form.querySelector('[name="id"]');
    if (!variantInput) throw new Error('Main product variant input not found.');
    const variantId = parseInt(variantInput.value, 10);
    if (!variantId) throw new Error('Please select a valid product variant.');
    return variantId;
  }

  function getMainQuantity(form) {
    const quantityInput = form.querySelector('[name="quantity"]');
    const quantity = parseInt(quantityInput?.value || '1', 10);
    return Number.isInteger(quantity) && quantity > 0 ? quantity : 1;
  }

  function queryFirst(selectors, root = document) {
    for (const selector of selectors) {
      const element = root.querySelector(selector);
      if (element) return element;
    }
    return null;
  }

  function queryAllUnique(selectors, root = document) {
    const elements = [];
    selectors.forEach((selector) => {
      root.querySelectorAll(selector).forEach((element) => {
        if (!elements.includes(element)) elements.push(element);
      });
    });
    return elements;
  }

  function readVariantId(element) {
    if (!element) return null;
    const possibleValues = [
      element.dataset.variantId,
      element.dataset.digitalGiftVariantId,
      element.dataset.giftVariantId,
      element.dataset.optionalProductVariantId,
      element.value
    ];
    for (const value of possibleValues) {
      const variantId = parseInt(value, 10);
      if (variantId) return variantId;
    }
    const parent = element.closest('[data-variant-id]');
    if (parent && parent.dataset.variantId) {
      const parentVarId = parseInt(parent.dataset.variantId, 10);
      if (parentVarId) return parentVarId;
    }
    return null;
  }

  function getGiftItems() {
    const giftElements = queryAllUnique(GIFT_VARIANT_SELECTORS);
    const usedVariantIds = new Set();
    const giftItems = [];

    giftElements.forEach((element) => {
      const variantId = readVariantId(element);
      if (!variantId || usedVariantIds.has(variantId)) return;
      usedVariantIds.add(variantId);
      giftItems.push({
        id: variantId,
        quantity: 1,
        properties: { '_Digital gift': 'Yes' }
      });
    });

    return giftItems;
  }

  function getOptionalItem() {
    const checkbox = queryFirst(OPTIONAL_CHECKBOX_SELECTORS);
    if (!checkbox || !checkbox.checked || checkbox.disabled) return null;

    let variantId = readVariantId(checkbox);
    if (!variantId) {
      const container = checkbox.closest('.order-optional') || document;
      const variantElement = queryFirst(OPTIONAL_VARIANT_SELECTORS, container);
      variantId = readVariantId(variantElement);
    }

    if (!variantId) {
      console.warn('[Order Extras] Optional product checked but variant ID not found.');
      return null;
    }

    return {
      id: variantId,
      quantity: 1,
      properties: { '_Optional product': 'Yes' }
    };
  }

  function buildCartItems(form) {
    const mainItem = {
      id: getSelectedMainVariantId(form),
      quantity: getMainQuantity(form)
    };

    if (window.productBOGOEnabled) {
      mainItem.quantity = mainItem.quantity * 2;
      mainItem.properties = { '_bogo': 'true' };
    }

    const cartItems = [mainItem, ...getGiftItems()];
    const optionalItem = getOptionalItem();
    if (optionalItem) cartItems.push(optionalItem);

    return cartItems;
  }

  function setLoading(form, loading) {
    const submitBtn = form.querySelector('[type="submit"]') || form.closest('product-form')?.querySelector('[type="submit"]');
    if (!submitBtn) return;
    submitBtn.disabled = loading;
    submitBtn.setAttribute('aria-disabled', loading ? 'true' : 'false');
    submitBtn.classList.toggle('loading', loading);
    const spinner = submitBtn.querySelector('.loading__spinner, .spinner');
    if (spinner) spinner.classList.toggle('hidden', !loading);
  }

  async function addItemsToCart(items) {
    const cartDrawer = document.querySelector('cart-drawer');
    const sections = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
      ? cartDrawer.getSectionsToRender().map((s) => s.id)
      : ['cart-drawer', 'cart-icon-bubble'];

    const response = await fetch(getCartAddUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items,
        sections,
        sections_url: window.location.pathname
      })
    });

    const responseData = await response.json();
    if (!response.ok || responseData.status) {
      throw new Error(responseData.description || 'Could not add items to cart.');
    }

    return responseData;
  }

  /* CRITICAL FIX: Unhide & Open Cart Drawer */
  async function renderCartDrawer(responseData) {
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer) {
      // Remove empty cart state class
      cartDrawer.classList.remove('is-empty');

      // Render updated drawer content
      if (typeof cartDrawer.renderContents === 'function' && responseData.sections) {
        cartDrawer.renderContents(responseData);
      }

      // Explicitly OPEN the drawer slider!
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open(document.activeElement);
      } else {
        cartDrawer.classList.add('active', 'animate');
      }
    }

    // Update Header Cart Count Icon
    if (responseData.sections && responseData.sections['cart-icon-bubble']) {
      const parser = new DOMParser();
      const htmlDoc = parser.parseFromString(responseData.sections['cart-icon-bubble'], 'text/html');
      const newBubble = htmlDoc.querySelector('#cart-icon-bubble');
      const currentBubble = document.querySelector('#cart-icon-bubble');
      if (newBubble && currentBubble) {
        currentBubble.innerHTML = newBubble.innerHTML;
      }
    }
  }

  async function handleProductFormSubmit(event) {
    const form = event.target.closest(PRODUCT_FORM_SELECTOR);
    if (!form) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (form.dataset.orderExtrasSubmitting === 'true') return;
    form.dataset.orderExtrasSubmitting = 'true';

    setLoading(form, true);

    try {
      const items = buildCartItems(form);
      const responseData = await addItemsToCart(items);

      // Render contents and open drawer immediately
      await renderCartDrawer(responseData);

      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'order-extras',
          cartData: responseData,
          productVariantId: items[0]?.id
        });
      }
    } catch (error) {
      console.error('[Order Extras]', error);
      alert(error.message || 'Error adding products to cart.');
    } finally {
      setLoading(form, false);
      form.dataset.orderExtrasSubmitting = 'false';
    }
  }

  document.addEventListener('submit', handleProductFormSubmit, true);
})();